'use client'

import { SubmitEventHandler, type ChangeEvent } from 'react'

import Link from 'next/link'
import { useUserStore } from '@/store/user'
import { formatPhone, formatPhoneToApi } from '@/utils/formatPhone'
import { useRouter, useSearchParams } from 'next/navigation'
import { AuthApi } from '@/api/auth'
import { ErrorText } from '@/components/Error/Error'
import { AuthErrors } from '@/config/errors'

export default function SigninPage() {
    const { phone, setPhone, setError, error } = useUserStore()
    const router = useRouter()
    const searchParams = useSearchParams()

    const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
        const formatted = formatPhone(e.target.value.replace(/[^\d+]/g, ''))
        setPhone(formatted)
    }

    const handleSubmit: SubmitEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault()

        AuthApi.LoginTG({ phone: phone.replace(/[^\d+]/g, '') })
            .then((data) => {
                if ('tg_url' in data) {
                    if (data.tg_url) {
                        window.open(`https://${data.tg_url}`, '_blank')
                    }

                    const redirectParams = new URLSearchParams(searchParams.toString())
                    redirectParams.set('phone', formatPhoneToApi(phone))

                    if ('is_personal_data_processing_accepted' in data && 'is_privacy_policy_accepted' in data) {
                        redirectParams.set(
                            'is_personal_data_processing_accepted',
                            String(data.is_personal_data_processing_accepted)
                        )
                        redirectParams.set(
                            'is_privacy_policy_accepted',
                            String(data.is_privacy_policy_accepted)
                        )
                    }

                    router.push(`${process.env.LK_URL}/auth?${redirectParams.toString()}`)
                } else {
                    router.push('/auth/signup')
                }
            })
            .catch((reject) => {
                if (typeof reject === 'object') {
                    if (reject.response?.data?.error && reject.response?.data?.error in AuthErrors) {
                        setTimeout(() => router.push('/auth/signup'), 2000)
                    } else if ('digest' in reject) {
                        router.push('/auth/confirm')
                    }
                }

                setError('Пожалуйста, сначала пройдите регистрацию')
            })
    }

    return (
        <div className="w-full max-w-md">
            <Link className="flex items-center gap-2 mb-10 lg:hidden" href="/">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 text-muted-foreground"
                >
                    <path d="m12 19-7-7 7-7" />
                    <path d="M19 12H5" />
                </svg>
                <span className="text-sm text-muted-foreground">На главную</span>
            </Link>

            <h1 className="text-2xl font-bold mb-2">Вход в систему</h1>
            <p className="text-muted-foreground mb-8">Введите номер телефона для входа</p>

            <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                <div className="flex flex-col">
                    <label className="text-sm font-medium leading-none" htmlFor="phone">
                        Номер телефона
                    </label>
                    <input
                        type="tel"
                        className="flex h-10 w-full rounded-md border border-[#dfe3e7] bg-white px-3 py-2 text-sm text-[#333] mt-1.5 outline-none focus-visible:ring-2 focus-visible:ring-primary placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        id="phone"
                        value={phone}
                        onChange={handlePhoneChange}
                        placeholder="+7 (___) ___-__-__"
                        required
                    />
                </div>

                <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium bg-[rgb(90,0,224)] text-white border-none rounded-md h-11 px-8 w-full cursor-pointer transition-colors duration-150 hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
                    disabled={phone.length < 18}
                >
                    Получить SMS-код
                </button>
            </form>

            <ErrorText error={error} />

            <p className="text-center text-sm text-muted-foreground mt-6">
                Нет аккаунта?{' '}
                <Link href={'/auth/signup'} className="text-[rgb(90,0,224)] hover:opacity-90 font-medium hover:underline">
                    Зарегистрируйтесь
                </Link>
            </p>
        </div>
    )
}
